import React, { useState, useEffect } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiFile, FiDownload, FiEye, FiSearch, FiShoppingCart } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../../components/Table';
import {
    fetchCompanyId,
    fetchBAMSRequests,
    type BAMSRequest
} from '../service/purchaseOrderBAMSService';

export default function PurchaseOrderBAMS() {
    const pso = useAppSelector((s) => s.user.pso);
    const [requests, setRequests] = useState<BAMSRequest[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<BAMSRequest[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');
    const [loading, setLoading] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<BAMSRequest | null>(null);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [showToast, setShowToast] = useState(false);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [statuses, setStatuses] = useState<string[]>([]);
    const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
    const [selectedRequestForPO, setSelectedRequestForPO] = useState<BAMSRequest | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso' or 'incharge'
    const isBankUser = userRole === 'bank';

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid, isBankUser);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid, isBankUser]);

    // Fetch requests
    useEffect(() => {
        if (!companyId) return;

        const loadRequests = async () => {
            setLoading(true);
            try {
                const requestsList = await fetchBAMSRequests(companyId);
                setRequests(requestsList);

                // Extract unique statuses
                const uniqueStatuses = Array.from(
                    new Set(requestsList.map(req => req.status).filter((s): s is string => Boolean(s)))
                );
                setStatuses(uniqueStatuses);
            } catch (error) {
                console.error('Error fetching requests:', error);
                setToastMessage('Failed to fetch requests');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadRequests();
    }, [companyId]);

    // Filter requests
    useEffect(() => {
        let filtered = [...requests];

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(request =>
                request.requestId?.toLowerCase().includes(term) ||
                request.branchCode?.toLowerCase().includes(term) ||
                request.branchName?.toLowerCase().includes(term) ||
                request.company?.toLowerCase().includes(term) ||
                request.requesterName?.toLowerCase().includes(term)
            );
        }

        // Status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(request => request.status === statusFilter);
        }

        // Date filter
        if (dateFilter !== 'All') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            filtered = filtered.filter(request => {
                if (!request.createdAt) return false;
                let requestDate: Date;
                if (request.createdAt.toDate) {
                    requestDate = request.createdAt.toDate();
                } else if (request.createdAt.seconds) {
                    requestDate = new Date(request.createdAt.seconds * 1000);
                } else {
                    requestDate = new Date(request.createdAt);
                }

                const requestDateOnly = new Date(requestDate.getFullYear(), requestDate.getMonth(), requestDate.getDate());

                switch (dateFilter) {
                    case 'Daily':
                        return requestDateOnly.getTime() === today.getTime();
                    case 'Weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return requestDateOnly.getTime() >= weekAgo.getTime();
                    case 'Monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return requestDateOnly.getTime() >= monthAgo.getTime();
                    default:
                        return true;
                }
            });
        }

        setFilteredRequests(filtered);
        setCurrentPage(1);
    }, [requests, searchTerm, statusFilter, dateFilter]);

    const handleViewDetails = (request: BAMSRequest) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleCreatePurchaseOrder = (request: BAMSRequest) => {
        setSelectedRequestForPO(request);
        setShowPurchaseOrderModal(true);
    };

    const handleGeneratePurchaseOrder = () => {
        // TODO: Implement purchase order generation
        setToastMessage('Purchase Order generated successfully!');
        setToastType('success');
        setShowToast(true);
        setShowPurchaseOrderModal(false);
        setSelectedRequestForPO(null);
    };

    const formatDateTime = (date: any) => {
        if (!date) return 'N/A';
        try {
            const dateObj = date.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const getStatusBadge = (status?: string) => {
        if (!status) return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">N/A</span>;

        const statusLower = status.toLowerCase();
        if (statusLower.includes('pending')) {
            return <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">Pending</span>;
        } else if (statusLower.includes('approved') || statusLower.includes('completed')) {
            return <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/20 dark:text-green-300">Approved</span>;
        } else if (statusLower.includes('rejected')) {
            return <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-300">Rejected</span>;
        } else if (statusLower.includes('cancelled')) {
            return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">Cancelled</span>;
        } else if (statusLower.includes('progress')) {
            return <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">In Progress</span>;
        } else if (statusLower.includes('forwarded')) {
            return <span className="rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-900/20 dark:text-purple-300">Forwarded</span>;
        }
        return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-300">{status}</span>;
    };

    const exportToExcel = () => {
        try {
            const dataToExport: any[] = [];

            filteredRequests.forEach((req, index) => {
                // Add PO Header Row
                dataToExport.push({
                    'Type': 'PURCHASE ORDER',
                    'Request ID': req.requestId || 'N/A',
                    'Branch Code': req.branchCode || 'N/A',
                    'Branch Name': req.branchName || 'N/A',
                    'Company': req.company || 'N/A',
                    'Requester Name': req.requesterName || 'N/A',
                    'Status': req.status || 'N/A',
                    'Created At': formatDateTime(req.createdAt),
                    'Item Code': '',
                    'Hardware Name': '',
                    'Quantity': ''
                });

                // Add Items
                const items = req.items && Array.isArray(req.items) && req.items.length > 0 ? req.items : null;

                if (items) {
                    items.forEach((item: any) => {
                        dataToExport.push({
                            'Type': 'ITEM',
                            'Request ID': '',
                            'Branch Code': '',
                            'Branch Name': '',
                            'Company': '',
                            'Requester Name': '',
                            'Status': '',
                            'Created At': '',
                            'Item Code': item.itemCode || 'N/A',
                            'Hardware Name': item.itemName || req.hardwareName || 'N/A',
                            'Quantity': item.quantity || 1
                        });
                    });
                } else {
                    dataToExport.push({
                        'Type': 'ITEM',
                        'Request ID': '',
                        'Branch Code': '',
                        'Branch Name': '',
                        'Company': '',
                        'Requester Name': '',
                        'Status': '',
                        'Created At': '',
                        'Item Code': 'N/A',
                        'Hardware Name': req.hardwareName || 'N/A',
                        'Quantity': 1
                    });
                }

                // Add empty row between POs (except after last one)
                if (index < filteredRequests.length - 1) {
                    dataToExport.push({
                        'Type': '',
                        'Request ID': '',
                        'Branch Code': '',
                        'Branch Name': '',
                        'Company': '',
                        'Requester Name': '',
                        'Status': '',
                        'Created At': '',
                        'Item Code': '',
                        'Hardware Name': '',
                        'Quantity': ''
                    });
                }
            });

            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'BAMS Purchase Orders');
            XLSX.writeFile(wb, `BAMS_Purchase_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);

            setToastMessage('Excel exported successfully');
            setToastType('success');
            setShowToast(true);
        } catch (error) {
            console.error('Excel Export Error:', error);
            setToastMessage('Failed to export Excel');
            setToastType('error');
            setShowToast(true);
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF();
            doc.text('BAMS Purchase Orders Report', 14, 15);

            const tableData = filteredRequests.map(req => [
                req.requestId || 'N/A',
                req.branchCode || 'N/A',
                req.company || 'N/A',
                req.hardwareName || 'N/A',
                req.requesterName || 'N/A',
                req.status || 'N/A'
            ]);

            autoTable(doc, {
                head: [['Request ID', 'Branch', 'Company', 'Hardware', 'Requester', 'Status']],
                body: tableData,
                startY: 20,
                styles: { fontSize: 8 },
                headStyles: { fillColor: [41, 128, 185] }
            });

            doc.save(`BAMS_Purchase_Orders_${new Date().toISOString().split('T')[0]}.pdf`);

            setToastMessage('PDF exported successfully');
            setToastType('success');
            setShowToast(true);
        } catch (error) {
            console.error('PDF Export Error:', error);
            setToastMessage('Failed to export PDF');
            setToastType('error');
            setShowToast(true);
        }
    };

    const columns: Column<BAMSRequest>[] = [
        {
            header: 'Request ID',
            accessor: 'requestId',
            render: (row) => <span className="font-medium">{row.requestId || 'N/A'}</span>
        },
        {
            header: 'Branch',
            accessor: 'branchCode',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{row.branchName || 'N/A'}</span>
                    <span className="text-xs text-gray-500">{row.branchCode || 'N/A'}</span>
                </div>
            )
        },
        {
            header: 'Company',
            accessor: 'company',
            render: (row) => row.company || 'N/A'
        },
        {
            header: 'Hardware Name',
            accessor: 'hardwareName',
            render: (row) => row.hardwareName || 'N/A'
        },
        {
            header: 'Requester',
            accessor: 'requesterName',
            render: (row) => row.requesterName || 'N/A'
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (row) => getStatusBadge(row.status)
        },
        {
            header: 'Actions',
            className: 'text-right',
            render: (row) => (
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => handleViewDetails(row)}
                        className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => handleCreatePurchaseOrder(row)}
                        className="rounded-lg p-2 text-green-600 transition hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                        title="Create Purchase Order"
                    >
                        <Icon icon={FiShoppingCart} className="h-5 w-5" />
                    </button>
                </div>
            )
        }
    ];

    const totalPages = Math.ceil(filteredRequests.length / perPage);
    const paginatedData = filteredRequests.slice((currentPage - 1) * perPage, currentPage * perPage);

    return (
        <div className="space-y-6 p-6">
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toastMessage}
                            type={toastType}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}

            <Table
                title="Purchase Order - BAMS"
                description="Manage BAMS purchase orders"
                data={paginatedData}
                columns={columns}
                keyExtractor={(item) => item.id || Math.random().toString()}
                loading={loading}
                pagination={{
                    currentPage,
                    totalPages,
                    onPageChange: setCurrentPage
                }}
                headerActions={
                    <div className="flex gap-3">
                        <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2 text-sm">
                            <Icon icon={FiFile} className="h-4 w-4" />
                            Export Excel
                        </Button>
                        <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2 text-sm">
                            <Icon icon={FiDownload} className="h-4 w-4" />
                            Export PDF
                        </Button>
                    </div>
                }
                filters={
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <div className="relative">
                            <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by ID, branch, company..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        >
                            <option value="All">All Statuses</option>
                            {statuses.map(status => (
                                <option key={status} value={status}>{status}</option>
                            ))}
                        </select>
                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                        >
                            <option value="All">All Time</option>
                            <option value="Daily">Daily</option>
                            <option value="Weekly">Weekly</option>
                            <option value="Monthly">Monthly</option>
                        </select>
                    </div>
                }
            />

            {/* Details Modal */}
            {showDetailsModal && selectedRequest && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedRequest(null);
                    }}
                    title={`BAMS Request Details - ${selectedRequest.requestId || selectedRequest.id}`}
                    size="lg"
                >
                    <div className="p-6">
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request ID</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestId || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Request Type</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">BAMS</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.branchName || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Company</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.company || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hardware Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.hardwareName || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterName || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Requester Contact</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requesterContact || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                    <p className="mt-1">{getStatusBadge(selectedRequest.status)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Created At</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(selectedRequest.createdAt)}</p>
                                </div>
                            </div>
                            {selectedRequest.purpose && (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Purpose</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.purpose}</p>
                                </div>
                            )}
                            {selectedRequest.remarks && (
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.remarks}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Purchase Order Modal */}
            {showPurchaseOrderModal && selectedRequestForPO && (
                <Modal
                    isOpen={showPurchaseOrderModal}
                    onClose={() => {
                        setShowPurchaseOrderModal(false);
                        setSelectedRequestForPO(null);
                    }}
                    title={`Create Purchase Order - ${selectedRequestForPO.requestId || selectedRequestForPO.id}`}
                    size="lg"
                >
                    <div className="p-6">
                        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                                Purchase Order will be generated for:
                            </p>
                            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                Request ID: {selectedRequestForPO.requestId || 'N/A'}
                            </p>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                Company: {selectedRequestForPO.company || 'N/A'}
                            </p>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                Hardware: {selectedRequestForPO.hardwareName || 'N/A'}
                            </p>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                Branch: {selectedRequestForPO.branchCode || 'N/A'} - {selectedRequestForPO.branchName || 'N/A'}
                            </p>
                        </div>
                        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowPurchaseOrderModal(false);
                                    setSelectedRequestForPO(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleGeneratePurchaseOrder}
                                className="flex items-center gap-2"
                            >
                                <Icon icon={FiShoppingCart} className="h-4 w-4" />
                                Generate Purchase Order
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

